/**
 * @description
 * As a music editor, I want a quick WAV download and download options on the track row, so that I can take a track without leaving the list.
 */
import { sendAnalytics } from '@audio-network/analytics'
import { Tooltip } from '@audio-network/componentlibrary'
import {
	ChevronDown,
	Download as DownloadIcon,
} from '@audio-network/componentlibrary/icons'
import clsx from 'clsx'
import { useTranslation } from 'next-i18next'
import { useId } from 'react'

import { keepDownloadCentreVisible } from '@/components/layout/Header/DownloadCentre/DownloadCentre.utils'
import DownloadOptionsModal, {
	useDownloadTrack,
} from '@/components/shared/DownloadOptionsModal'

import styles from './Download.module.css'
import { DownloadOffScreenProps, DownloadProps } from './Download.types'

export const DownloadOffScreen = ({
	className,
	showDownloadOptions = true,
	...props
}: DownloadOffScreenProps): JSX.Element => {
	const { t } = useTranslation(['download'])
	const wavLabel = t('wav')
	const downloadOptionsLabel = t('download-options')

	return (
		<span
			className={clsx(
				styles.downloadWrapper,
				showDownloadOptions && styles.withDownloadOptions,
				className
			)}
			{...props}
		>
			<span className={clsx(styles.downloadButton, styles.quickButton)}>
				<DownloadIcon className={clsx(styles.icon, styles.downloadIcon)} />
				<span className={styles.buttonText}>{wavLabel}</span>
			</span>
			{showDownloadOptions && (
				<>
					<span className={styles.verticalDivider} />
					<span className={styles.downloadButton}>
						<span className={styles.buttonText}>{downloadOptionsLabel}</span>
						<ChevronDown
							className={clsx(styles.icon, styles.chevronDownIcon)}
						/>
					</span>
				</>
			)}
		</span>
	)
}

const Download = ({
	className,
	source,
	location,
	track,
	zipFileName,
	showDownloadOptions = true,
	...props
}: DownloadProps): JSX.Element => {
	// Strip React useId colons — invoker commandfor / attribute selectors break on `:`.
	const downloadOptionsModalId = `track-row-download-modal-${useId().replaceAll(
		':',
		''
	)}`
	const resolvedZipFileName = zipFileName?.trim() || source
	const { downloadTrack, isDownloading } = useDownloadTrack(
		source,
		resolvedZipFileName,
		track.id
	)
	const { t } = useTranslation(['download'])
	const wavLabel = t('wav')
	const downloadOptionsLabel = t('download-options')

	const handleDownload = (): void => {
		keepDownloadCentreVisible()
		void downloadTrack(track, 'wav')
	}

	const handleDownloadOptionsOpen = (): void => {
		sendAnalytics({
			noun: 'Download Options',
			pastTenseVerb: 'Clicked',
			eventParameters: {
				Location: location,
			},
		})
	}

	return (
		<>
			<span
				className={clsx(
					styles.downloadWrapper,
					showDownloadOptions && styles.withDownloadOptions,
					className
				)}
				{...props}
				onMouseEnter={keepDownloadCentreVisible}
			>
				<Tooltip content={wavLabel}>
					<button
						className={clsx(styles.downloadButton, styles.quickButton)}
						disabled={isDownloading}
						onClick={handleDownload}
						type="button"
					>
						<DownloadIcon className={clsx(styles.icon, styles.downloadIcon)} />
						<span className={styles.buttonText}>{wavLabel}</span>
					</button>
				</Tooltip>

				{showDownloadOptions && (
					<>
						<span className={styles.verticalDivider} />
						<Tooltip content={downloadOptionsLabel}>
							<button
								className={styles.downloadButton}
								data-testid="download-options-button"
								onClick={handleDownloadOptionsOpen}
								{...{
									command: 'show-modal',
									commandfor: downloadOptionsModalId,
								}}
								type="button"
							>
								<span className={styles.buttonText}>
									{downloadOptionsLabel}
								</span>
								<ChevronDown
									className={clsx(styles.icon, styles.chevronDownIcon)}
								/>
							</button>
						</Tooltip>
					</>
				)}
			</span>

			{showDownloadOptions && (
				<DownloadOptionsModal
					modalId={downloadOptionsModalId}
					trackSource={source}
					tracks={[track]}
					zipFileName={zipFileName}
				/>
			)}
		</>
	)
}

export default Download
